<template>
  <div class="almacen-page">

    <!-- Header -->
    <div class="alm-header">
      <div>
        <span class="header-eyebrow">Inventario</span>
        <h2 class="header-title">Almacén RFID</h2>
      </div>
      <div class="scan-box">
        <i class="fa-solid fa-tower-broadcast scan-box__icon"></i>
        <input
          class="scan-box__input"
          placeholder="Escanear o buscar RFID..."
          v-model="rfidBuscar"
          @keyup.enter="buscarRfid"
        />
        <button class="scan-box__btn" @click="buscarRfid">
          <i class="fa-solid fa-magnifying-glass"></i>
        </button>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">Estanterías</span>
        <span class="kpi-value">{{ listEstanterias.length }}</span>
        <span class="kpi-foot">activas</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Cajas</span>
        <span class="kpi-value">{{ listCajas.length }}</span>
        <span class="kpi-foot">con RFID</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Unidades</span>
        <span class="kpi-value">{{ listUnidades.length }}</span>
        <span class="kpi-foot">en almacén</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Materiales</span>
        <span class="kpi-value">{{ materialesDistintos }}</span>
        <span class="kpi-foot">distintos</span>
      </div>
    </div>

    <!-- Resultado de escaneo RFID -->
    <transition name="slide">
      <div class="scan-result" v-if="rfidResultado">
        <div class="scan-result__icon" :class="'scan-result__icon--' + rfidResultado.tipo">
          <i :class="iconoResultado"></i>
        </div>
        <div class="scan-result__body">
          <template v-if="rfidResultado.tipo == 'estanteria'">
            <span class="scan-result__title">Estantería · {{ rfidResultado.estanteria.nombre }}</span>
            <span class="scan-result__meta">
              <i class="fa-solid fa-layer-group"></i>
              {{ rfidResultado.estanteria.filas }} filas · {{ rfidResultado.estanteria.numCajas }} cajas · {{ rfidResultado.estanteria.numUnidadesSueltas }} unidades sueltas
            </span>
          </template>

          <template v-if="rfidResultado.tipo == 'caja'">
            <span class="scan-result__title">Caja · {{ rfidResultado.caja.nombre || rfidResultado.caja.rfid }}</span>
            <span class="scan-result__meta">
              <i class="fa-solid fa-layer-group"></i> {{ rfidResultado.caja.estanteria }}, fila {{ rfidResultado.caja.fila }}
            </span>
            <div class="scan-result__list" v-if="rfidResultado.unidades.length > 0">
              <div class="scan-item" v-for="u in rfidResultado.unidades" :key="u.id">
                <span class="scan-item__name">{{ u.material }}</span>
                <span class="qty-pill" :class="Number(u.cantidad) > 0 ? 'qty-pill--ok' : 'qty-pill--zero'">
                  {{ fmt(u.cantidad) }} {{ u.tipoMedidas }}
                </span>
                <span class="rfid-chip">{{ u.rfid }}</span>
              </div>
            </div>
            <span class="scan-result__empty" v-else>La caja está vacía</span>
          </template>

          <template v-if="rfidResultado.tipo == 'unidad'">
            <span class="scan-result__title">{{ rfidResultado.unidad.material }}</span>
            <span class="scan-result__meta">
              <span class="qty-pill" :class="Number(rfidResultado.unidad.cantidad) > 0 ? 'qty-pill--ok' : 'qty-pill--zero'">
                {{ fmt(rfidResultado.unidad.cantidad) }} {{ rfidResultado.unidad.tipoMedidas }}
              </span>
              <span class="loc-chip">
                <i :class="rfidResultado.unidad.idCaja ? 'fa-solid fa-box' : 'fa-solid fa-layer-group'"></i>
                {{ ubicacionTexto(rfidResultado.unidad) }}
              </span>
            </span>
            <div class="scan-result__actions">
              <button class="btn-alm btn-alm--danger-soft" @click="abrirMovimiento(rfidResultado.unidad, 'descontar')">
                <i class="fa-solid fa-minus"></i> Descontar
              </button>
              <button class="btn-alm btn-alm--success-soft" @click="abrirMovimiento(rfidResultado.unidad, 'reponer')">
                <i class="fa-solid fa-plus"></i> Reponer
              </button>
            </div>
          </template>

          <template v-if="rfidResultado.tipo == 'no-encontrado'">
            <span class="scan-result__title">RFID no encontrado</span>
            <span class="scan-result__meta"><span class="rfid-chip">{{ rfidResultado.rfid }}</span></span>
          </template>
        </div>
        <button class="scan-result__close" @click="rfidResultado = null">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    </transition>

    <!-- Tabs -->
    <div class="alm-tabs">
      <button
        v-for="(tab, i) in tabs"
        :key="tab.label"
        class="alm-tab"
        :class="{ 'alm-tab--active': tabIndex == i }"
        @click="tabIndex = i"
      >
        <i :class="tab.icon"></i> {{ tab.label }}
      </button>
    </div>

    <!-- ============ TAB STOCK / UNIDADES ============ -->
    <div class="list-card" v-show="tabIndex == 0">
      <div class="list-card__header">
        <div class="search-box">
          <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
          <input class="search-box__input" placeholder="Buscar por id, material, color o RFID..." v-model="buscarUnidad" />
        </div>
        <div class="header-actions">
          <button class="btn-alm btn-alm--soft" @click="abrirHistorialForm">
            <i class="fa-solid fa-clock-rotate-left"></i> Añadir historial
          </button>
          <button class="btn-alm btn-alm--primary" @click="abrirUnidadForm(null)">
            <i class="fa-solid fa-plus"></i> Nueva unidad
          </button>
        </div>
      </div>

      <b-table
        :items="unidadesFiltradas"
        :fields="columnasUnidades"
        thead-class="table-head"
        tbody-tr-class="table-row"
        responsive
        show-empty
      >
        <template #empty>
          <div class="empty-state">
            <i class="fa-solid fa-tag"></i>
            <span>No hay unidades registradas</span>
          </div>
        </template>
        <template #cell(id)="data">
          <span class="cell-mono">#{{ data.item.id }}</span>
        </template>
        <template #cell(material)="data">
          <div class="cell-nombre">
            <span class="cell-nombre__name">{{ data.item.material }}</span>
            <span class="cell-nombre__sub" v-if="data.item.color">{{ data.item.color }}</span>
          </div>
        </template>
        <template #cell(cantidad)="data">
          <span class="qty-pill" :class="Number(data.item.cantidad) > 0 ? 'qty-pill--ok' : 'qty-pill--zero'">
            {{ fmt(data.item.cantidad) }} {{ data.item.tipoMedidas }}
          </span>
        </template>
        <template #cell(ubicacion)="data">
          <span class="loc-chip">
            <i :class="data.item.idCaja ? 'fa-solid fa-box' : 'fa-solid fa-layer-group'"></i>
            {{ ubicacionTexto(data.item) }}
          </span>
        </template>
        <template #cell(nota)="data">
          <span class="cell-muted">{{ data.item.nota || '—' }}</span>
        </template>
        <template #cell(acciones)="data">
          <div class="cell-actions">
            <button class="action-btn action-btn--delete" v-b-tooltip.hover.top title="Descontar" @click="abrirMovimiento(data.item, 'descontar')">
              <i class="fa-solid fa-minus"></i>
            </button>
            <button class="action-btn action-btn--success" v-b-tooltip.hover.top title="Reponer" @click="abrirMovimiento(data.item, 'reponer')">
              <i class="fa-solid fa-plus"></i>
            </button>
            <button class="action-btn action-btn--warning" v-b-tooltip.hover.top title="Ajustar cantidad" @click="abrirMovimiento(data.item, 'ajustar')">
              <i class="fa-solid fa-scale-balanced"></i>
            </button>
            <button class="action-btn action-btn--print" v-b-tooltip.hover.top title="Imprimir etiqueta" @click="imprimirUnidad(data.item)">
              <i class="fa-solid fa-print"></i>
            </button>
            <button class="action-btn action-btn--edit" v-b-tooltip.hover.top title="Editar / mover" @click="abrirUnidadForm(data.item)">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button class="action-btn action-btn--view" v-b-tooltip.hover.top title="Historial" @click="verHistorialUnidad(data.item)">
              <i class="fa-solid fa-clock-rotate-left"></i>
            </button>
            <button class="action-btn action-btn--delete" v-b-tooltip.hover.top title="Eliminar" @click="eliminarUnidad(data.item)">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </template>
      </b-table>

      <div class="list-card__footer">
        <span class="footer-count">{{ unidadesFiltradas.length }} unidades</span>
      </div>
    </div>

    <!-- ============ TAB ESTANTERIAS ============ -->
    <div class="list-card" v-show="tabIndex == 1">
      <div class="list-card__header">
        <div class="search-box">
          <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
          <input class="search-box__input" placeholder="Buscar estantería..." v-model="buscarEstanteria" />
        </div>
        <button class="btn-alm btn-alm--primary" @click="abrirEstanteriaForm(null)">
          <i class="fa-solid fa-plus"></i> Nueva estantería
        </button>
      </div>

      <b-table
        :items="estanteriasFiltradas"
        :fields="columnasEstanterias"
        thead-class="table-head"
        tbody-tr-class="table-row"
        responsive
        show-empty
      >
        <template #empty>
          <div class="empty-state">
            <i class="fa-solid fa-layer-group"></i>
            <span>No hay estanterías creadas</span>
          </div>
        </template>
        <template #cell(nombre)="data">
          <div class="cell-nombre">
            <span class="cell-nombre__name">{{ data.item.nombre }}</span>
          </div>
        </template>
        <template #cell(filas)="data">
          <span class="badge-count">{{ data.item.filas }} filas</span>
        </template>
        <template #cell(numCajas)="data">
          <span class="cell-mono">{{ data.item.numCajas }}</span>
        </template>
        <template #cell(numUnidadesSueltas)="data">
          <span class="cell-mono">{{ data.item.numUnidadesSueltas }}</span>
        </template>
        <template #cell(descripcion)="data">
          <span class="cell-muted">{{ data.item.descripcion || '—' }}</span>
        </template>
        <template #cell(acciones)="data">
          <div class="cell-actions">
            <button class="action-btn action-btn--print" v-b-tooltip.hover.top title="Imprimir etiqueta" @click="imprimirEstanteria(data.item)">
              <i class="fa-solid fa-print"></i>
            </button>
            <button class="action-btn action-btn--edit" v-b-tooltip.hover.top title="Editar" @click="abrirEstanteriaForm(data.item)">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button class="action-btn action-btn--delete" v-b-tooltip.hover.top title="Eliminar" @click="eliminarEstanteria(data.item)">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </template>
      </b-table>

      <div class="list-card__footer">
        <span class="footer-count">{{ estanteriasFiltradas.length }} estanterías</span>
      </div>
    </div>

    <!-- ============ TAB CAJAS ============ -->
    <div class="list-card" v-show="tabIndex == 2">
      <div class="list-card__header">
        <div class="filter-group">
          <v-select
            v-model="filtroEstanteriaCajas"
            label="nombre"
            :options="listEstanterias"
            placeholder="Filtrar por estantería"
            class="select-styled select-filter"
          />
        </div>
        <button class="btn-alm btn-alm--primary" @click="abrirCajaForm(null)">
          <i class="fa-solid fa-plus"></i> Nueva caja
        </button>
      </div>

      <b-table
        :items="cajasFiltradas"
        :fields="columnasCajas"
        thead-class="table-head"
        tbody-tr-class="table-row"
        responsive
        show-empty
      >
        <template #empty>
          <div class="empty-state">
            <i class="fa-solid fa-box-open"></i>
            <span>No hay cajas creadas</span>
          </div>
        </template>
        <template #cell(nombre)="data">
          <div class="cell-nombre">
            <span class="cell-nombre__name">{{ data.item.nombre || 'Sin nombre' }}</span>
          </div>
        </template>
        <template #cell(rfid)="data">
          <span class="rfid-chip">{{ data.item.rfid }}</span>
        </template>
        <template #cell(estanteria)="data">
          <span class="loc-chip">
            <i class="fa-solid fa-layer-group"></i>
            {{ data.item.estanteria }}, fila {{ data.item.fila }}
          </span>
        </template>
        <template #cell(numUnidades)="data">
          <span class="badge-count" :class="{ 'badge-count--empty': data.item.numUnidades == 0 }">
            {{ data.item.numUnidades }} {{ data.item.numUnidades == 1 ? 'unidad' : 'unidades' }}
          </span>
        </template>
        <template #cell(acciones)="data">
          <div class="cell-actions">
            <button class="action-btn action-btn--view" v-b-tooltip.hover.top title="Ver contenido" @click="verContenidoCaja(data.item)">
              <i class="fa-solid fa-box-open"></i>
            </button>
            <button class="action-btn action-btn--print" v-b-tooltip.hover.top title="Imprimir etiqueta" @click="imprimirCaja(data.item)">
              <i class="fa-solid fa-print"></i>
            </button>
            <button class="action-btn action-btn--edit" v-b-tooltip.hover.top title="Editar" @click="abrirCajaForm(data.item)">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button class="action-btn action-btn--delete" v-b-tooltip.hover.top title="Eliminar" @click="eliminarCaja(data.item)">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </template>
      </b-table>

      <div class="list-card__footer">
        <span class="footer-count">{{ cajasFiltradas.length }} cajas</span>
      </div>
    </div>

    <!-- ============ TAB HISTORIAL ============ -->
    <div class="list-card" v-show="tabIndex == 3">
      <div class="list-card__header">
        <div class="list-card__filters">
          <div class="filter-group">
            <v-select
              v-model="filtroHistMaterial"
              label="nombre"
              :options="listMateriales"
              placeholder="Filtrar por material"
              class="select-styled select-filter"
              @input="loadHistorial"
            />
          </div>
          <div class="filter-group">
            <v-select
              v-model="filtroHistTipo"
              :options="tiposHistorial"
              placeholder="Tipo"
              class="select-styled select-filter select-filter--small"
              @input="loadHistorial"
            />
          </div>
        </div>
      </div>

      <b-table
        :items="listHistorial"
        :fields="columnasHistorial"
        thead-class="table-head"
        tbody-tr-class="table-row"
        responsive
        show-empty
      >
        <template #empty>
          <div class="empty-state">
            <i class="fa-solid fa-clock-rotate-left"></i>
            <span>Sin movimientos registrados</span>
          </div>
        </template>
        <template #cell(fecha)="data">
          <span class="cell-mono">{{ data.item.fecha }}</span>
        </template>
        <template #cell(material)="data">
          <div class="cell-nombre">
            <span class="cell-nombre__name">{{ data.item.material }}</span>
          </div>
        </template>
        <template #cell(tipo)="data">
          <span class="tipo-pill" :class="'tipo-pill--' + data.item.tipo">{{ data.item.tipo }}</span>
        </template>
        <template #cell(cantidad)="data">
          <span class="cell-mono" v-if="data.item.cantidad != null">{{ fmt(data.item.cantidad) }} {{ data.item.tipoMedidas }}</span>
          <span class="cell-muted" v-else>—</span>
        </template>
        <template #cell(stock)="data">
          <span class="cell-mono" v-if="data.item.cantidadDespues != null">{{ fmt(data.item.cantidadAntes) }} <i class="fa-solid fa-arrow-right-long stock-arrow"></i> {{ fmt(data.item.cantidadDespues) }}</span>
          <span class="cell-muted" v-else>—</span>
        </template>
        <template #cell(usuario)="data">
          <span class="cell-text">{{ data.item.usuario || '—' }}</span>
        </template>
        <template #cell(detalle)="data">
          <span class="cell-muted">{{ data.item.detalle || '—' }}</span>
        </template>
      </b-table>

      <div class="list-card__footer">
        <span class="footer-count">{{ listHistorial.length }} movimientos</span>
      </div>
    </div>

    <!-- ============ MODAL ESTANTERIA ============ -->
    <b-modal v-model="showEstanteriaModal" modal-class="modal-alm" :title="estanteriaForm.id ? 'Editar estantería' : 'Nueva estantería'" @ok.prevent="guardarEstanteria" ok-title="Guardar" cancel-title="Cancelar">
      <div class="modal-fields">
        <div class="mfield" v-if="estanteriaForm.id && estanteriaForm.rfid">
          <label class="mfield__label">RFID</label>
          <div><span class="rfid-chip rfid-chip--modal">{{ estanteriaForm.rfid }}</span></div>
        </div>
        <div class="auto-rfid" v-else-if="!estanteriaForm.id">
          <i class="fa-solid fa-wand-magic-sparkles"></i>
          El RFID se generará automáticamente al guardar
        </div>
        <div class="mfield">
          <label class="mfield__label">Nombre</label>
          <b-form-input v-model="estanteriaForm.nombre" placeholder="Ej: Estantería A" class="mfield__input" />
        </div>
        <div class="mfield">
          <label class="mfield__label">Número de filas</label>
          <b-form-input v-model.number="estanteriaForm.filas" type="number" min="1" class="mfield__input" />
        </div>
        <div class="mfield">
          <label class="mfield__label">Descripción</label>
          <b-form-input v-model="estanteriaForm.descripcion" placeholder="Opcional" class="mfield__input" />
        </div>
      </div>
    </b-modal>

    <!-- ============ MODAL AÑADIR HISTORIAL ============ -->
    <b-modal v-model="showHistModal" modal-class="modal-alm" size="lg" title="Añadir historial" @ok.prevent="guardarHistorial" :ok-disabled="guardandoHist" ok-title="Guardar" cancel-title="Cancelar">
      <div class="modal-fields">
        <div class="mfield">
          <label class="mfield__label">Unidad</label>
          <v-select v-model="histForm.unidadSelect" :options="listUnidades" :get-option-label="u => u.material + (u.color ? ' (' + u.color + ')' : '') + ' — ' + ubicacionTexto(u)" class="select-styled" />
        </div>

        <div class="hist-lineas">
          <div class="hist-lineas__head">
            <span>Fecha</span>
            <span>Tipo</span>
            <span>Cantidad</span>
            <span></span>
          </div>
          <div class="hist-linea" v-for="(l, i) in histForm.lineas" :key="i">
            <b-form-input type="date" v-model="l.fecha" class="mfield__input" />
            <v-select v-model="l.tipo" :options="tiposManual" :clearable="false" class="select-styled" />
            <b-form-input type="number" step="any" v-model.number="l.cantidad" placeholder="0" class="mfield__input" />
            <button type="button" class="hist-linea__del" @click="quitarLineaHist(i)" v-if="histForm.lineas.length > 1">
              <i class="fa-solid fa-xmark"></i>
            </button>
            <span v-else></span>
          </div>
          <button type="button" class="btn-alm btn-alm--soft hist-lineas__add" @click="agregarLineaHist">
            <i class="fa-solid fa-plus"></i> Añadir línea
          </button>
        </div>
      </div>
    </b-modal>

    <!-- ============ MODAL IMPRIMIR ETIQUETA ============ -->
    <b-modal v-model="showPrintModal" modal-class="modal-alm" title="Imprimir etiqueta" @ok.prevent="confirmarImprimir" :ok-disabled="imprimiendo" ok-title="Imprimir" cancel-title="Cerrar">
      <div class="print-preview">
        <img v-if="previewAlmacen" :src="previewAlmacen" class="print-preview__img" />
        <b-spinner v-else-if="cargandoPreview" small />
        <span v-else class="cell-muted">Sin vista previa</span>
      </div>
      <div class="auto-rfid auto-rfid--stack" v-if="printDatos.rfid">
        <div><i class="fa-solid fa-microchip"></i> El RFID se grabará en el chip de la etiqueta</div>
        <span class="rfid-chip rfid-chip--modal">{{ printDatos.rfid }}</span>
      </div>
    </b-modal>

    <!-- ============ MODAL CAJA ============ -->
    <b-modal v-model="showCajaModal" modal-class="modal-alm" :title="cajaForm.id ? 'Editar caja' : 'Nueva caja'" @ok.prevent="guardarCaja" ok-title="Guardar" cancel-title="Cancelar">
      <div class="modal-fields">
        <div class="mfield" v-if="cajaForm.id">
          <label class="mfield__label">RFID</label>
          <div><span class="rfid-chip rfid-chip--modal">{{ cajaForm.rfid }}</span></div>
        </div>
        <div class="auto-rfid" v-else>
          <i class="fa-solid fa-wand-magic-sparkles"></i>
          El RFID se generará automáticamente al guardar
        </div>
        <div class="mfield">
          <label class="mfield__label">Nombre</label>
          <b-form-input v-model="cajaForm.nombre" placeholder="Ej: Caja herrajes 1" class="mfield__input" />
        </div>
        <div class="mfield">
          <label class="mfield__label">Estantería</label>
          <v-select v-model="cajaForm.estanteriaSelect" label="nombre" :options="listEstanterias" class="select-styled" />
        </div>
        <div class="mfield">
          <label class="mfield__label">Fila {{ cajaForm.estanteriaSelect ? '(1 - ' + cajaForm.estanteriaSelect.filas + ')' : '' }}</label>
          <b-form-input v-model.number="cajaForm.fila" type="number" min="1" :max="cajaForm.estanteriaSelect ? cajaForm.estanteriaSelect.filas : null" class="mfield__input" />
        </div>
      </div>
    </b-modal>

    <!-- ============ MODAL UNIDAD ============ -->
    <b-modal v-model="showUnidadModal" modal-class="modal-alm" :title="unidadForm.id ? 'Editar / mover unidad' : 'Nueva unidad'" @ok.prevent="guardarUnidad" ok-title="Guardar" cancel-title="Cancelar">
      <div class="modal-fields">
        <div class="mfield" v-if="unidadForm.id">
          <label class="mfield__label">RFID</label>
          <div><span class="rfid-chip rfid-chip--modal">{{ unidadForm.rfid }}</span></div>
        </div>
        <div class="auto-rfid" v-else>
          <i class="fa-solid fa-wand-magic-sparkles"></i>
          El RFID se generará automáticamente al guardar
        </div>
        <div class="mfield" v-if="!unidadForm.id">
          <label class="mfield__label">Material</label>
          <v-select v-model="unidadForm.materialSelect" label="nombre" :options="listMateriales" class="select-styled" @input="onMaterialElegido">
            <template #option="option">
              {{ option.nombre }} <small v-if="option.color">({{ option.color }})</small>
            </template>
          </v-select>
        </div>
        <div class="mfield" v-if="!unidadForm.id">
          <label class="mfield__label">Cantidad inicial {{ unidadForm.materialSelect ? '· ' + unidadForm.materialSelect.tipoMedidas : '' }}</label>
          <b-form-input v-model.number="unidadForm.cantidad" type="number" min="0" step="any" class="mfield__input" />
        </div>
        <div class="mfield">
          <label class="mfield__label">Ubicación</label>
          <div class="seg-control">
            <button
              type="button"
              class="seg-control__btn"
              :class="{ 'seg-control__btn--active': unidadForm.ubicacionTipo == 'caja' }"
              @click="unidadForm.ubicacionTipo = 'caja'"
            >
              <i class="fa-solid fa-box"></i> En una caja
            </button>
            <button
              type="button"
              class="seg-control__btn"
              :class="{ 'seg-control__btn--active': unidadForm.ubicacionTipo == 'estanteria' }"
              @click="unidadForm.ubicacionTipo = 'estanteria'"
            >
              <i class="fa-solid fa-layer-group"></i> Suelta en estantería
            </button>
          </div>
        </div>
        <div class="mfield" v-if="unidadForm.ubicacionTipo == 'caja'">
          <label class="mfield__label">Caja</label>
          <v-select v-model="unidadForm.cajaSelect" :options="listCajas" :get-option-label="c => (c.nombre || c.rfid) + ' — ' + c.estanteria + ', fila ' + c.fila" class="select-styled" />
        </div>
        <template v-if="unidadForm.ubicacionTipo == 'estanteria'">
          <div class="mfield">
            <label class="mfield__label">Estantería</label>
            <v-select v-model="unidadForm.estanteriaSelect" label="nombre" :options="listEstanterias" class="select-styled" />
          </div>
          <div class="mfield">
            <label class="mfield__label">Fila {{ unidadForm.estanteriaSelect ? '(1 - ' + unidadForm.estanteriaSelect.filas + ')' : '' }}</label>
            <b-form-input v-model.number="unidadForm.fila" type="number" min="1" :max="unidadForm.estanteriaSelect ? unidadForm.estanteriaSelect.filas : null" class="mfield__input" />
          </div>
        </template>
        <div class="mfield">
          <label class="mfield__label">Nota</label>
          <b-form-input v-model="unidadForm.nota" placeholder="Opcional" class="mfield__input" />
        </div>
      </div>
    </b-modal>

    <!-- ============ MODAL DESCONTAR / REPONER / AJUSTAR ============ -->
    <b-modal v-model="showMovModal" modal-class="modal-alm" :title="tituloMovimiento" @ok.prevent="guardarMovimiento" :ok-title="tituloMovimiento" cancel-title="Cancelar">
      <div class="modal-fields">
        <div class="mov-info" v-if="movForm.unidad">
          <div class="mov-info__icon" :class="'mov-info__icon--' + movForm.tipo">
            <i :class="iconoMovimiento"></i>
          </div>
          <div>
            <span class="mov-info__name">{{ movForm.unidad.material }}</span>
            <span class="mov-info__meta">
              Quedan {{ fmt(movForm.unidad.cantidad) }} {{ movForm.unidad.tipoMedidas }} · {{ ubicacionTexto(movForm.unidad) }}
            </span>
          </div>
        </div>
        <div class="mfield">
          <label class="mfield__label">{{ movForm.tipo == 'ajustar' ? 'Cantidad real (recuento)' : 'Cantidad' }} {{ movForm.unidad ? '· ' + movForm.unidad.tipoMedidas : '' }}</label>
          <b-form-input v-model.number="movForm.cantidad" type="number" min="0" step="any" autofocus class="mfield__input mfield__input--big" />
        </div>
        <div class="mfield">
          <label class="mfield__label">Nota</label>
          <b-form-input v-model="movForm.nota" placeholder="Ej: para pedido 1234" class="mfield__input" />
        </div>
      </div>
    </b-modal>

    <!-- ============ MODAL CONTENIDO CAJA ============ -->
    <b-modal v-model="showContenidoModal" modal-class="modal-alm" :title="'Contenido de ' + (cajaContenido ? (cajaContenido.nombre || cajaContenido.rfid) : '')" ok-only ok-title="Cerrar">
      <div class="modal-fields">
        <div class="empty-state empty-state--modal" v-if="unidadesCaja.length == 0">
          <i class="fa-solid fa-box-open"></i>
          <span>La caja está vacía</span>
        </div>
        <div class="scan-item" v-for="u in unidadesCaja" :key="u.id">
          <span class="scan-item__name">{{ u.material }}</span>
          <span class="qty-pill" :class="Number(u.cantidad) > 0 ? 'qty-pill--ok' : 'qty-pill--zero'">
            {{ fmt(u.cantidad) }} {{ u.tipoMedidas }}
          </span>
          <span class="rfid-chip">{{ u.rfid }}</span>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import { VBTooltip } from 'bootstrap-vue'

export default {
  components: {
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      tabIndex: 0,
      tabs: [
        { label: 'Stock', icon: 'fa-solid fa-tag' },
        { label: 'Estanterías', icon: 'fa-solid fa-layer-group' },
        { label: 'Cajas', icon: 'fa-solid fa-box' },
        { label: 'Historial', icon: 'fa-solid fa-clock-rotate-left' },
      ],

      listEstanterias: [],
      listCajas: [],
      listUnidades: [],
      listMateriales: [],
      listHistorial: [],

      buscarUnidad: '',
      buscarEstanteria: '',
      filtroEstanteriaCajas: null,
      filtroHistMaterial: null,
      filtroHistTipo: null,
      tiposHistorial: ['alta', 'salida', 'entrada', 'movimiento', 'ajuste', 'baja', 'nota'],
      // El historial manual solo permite entrada o salida
      tiposManual: ['entrada', 'salida'],

      showHistModal: false,
      histForm: { unidadSelect: null, lineas: [] },
      guardandoHist: false,

      rfidBuscar: '',
      rfidResultado: null,

      showEstanteriaModal: false,
      estanteriaForm: { id: null, nombre: '', filas: 1, descripcion: '' },

      // Impresión de etiquetas (Zebra RFID, graba el chip)
      showPrintModal: false,
      printDatos: { titulo: '', sub: '', rfid: '' },
      previewAlmacen: null,
      cargandoPreview: false,
      imprimiendo: false,

      showCajaModal: false,
      cajaForm: { id: null, rfid: '', nombre: '', estanteriaSelect: null, fila: 1 },

      showUnidadModal: false,
      unidadForm: { id: null, rfid: '', materialSelect: null, cantidad: 0, ubicacionTipo: 'caja', cajaSelect: null, estanteriaSelect: null, fila: 1, nota: '' },

      showMovModal: false,
      movForm: { unidad: null, tipo: 'descontar', cantidad: null, nota: '' },

      showContenidoModal: false,
      cajaContenido: null,
      unidadesCaja: [],

      columnasUnidades: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'material', label: 'Material', sortable: true },
        { key: 'cantidad', label: 'Cantidad', sortable: true },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'nota', label: 'Nota' },
        { key: 'acciones', label: '', thStyle: { width: '230px' }, tdClass: 'text-nowrap' },
      ],
      columnasEstanterias: [
        { key: 'nombre', label: 'Nombre', sortable: true },
        { key: 'filas', label: 'Filas', sortable: true },
        { key: 'numCajas', label: 'Cajas', sortable: true },
        { key: 'numUnidadesSueltas', label: 'Unidades sueltas', sortable: true },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'acciones', label: '', thStyle: { width: '90px' }, tdClass: 'text-nowrap' },
      ],
      columnasCajas: [
        { key: 'nombre', label: 'Nombre', sortable: true },
        { key: 'rfid', label: 'RFID', sortable: true },
        { key: 'estanteria', label: 'Ubicación', sortable: true },
        { key: 'numUnidades', label: 'Contenido', sortable: true },
        { key: 'acciones', label: '', thStyle: { width: '120px' }, tdClass: 'text-nowrap' },
      ],
      columnasHistorial: [
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'material', label: 'Material', sortable: true },
        { key: 'tipo', label: 'Tipo', sortable: true },
        { key: 'cantidad', label: 'Cantidad' },
        { key: 'stock', label: 'Antes / Después' },
        { key: 'usuario', label: 'Usuario', sortable: true },
        { key: 'detalle', label: 'Detalle' },
      ],
    }
  },
  computed: {
    unidadesFiltradas() {
      if (!this.buscarUnidad) return this.listUnidades
      const b = this.buscarUnidad.toLowerCase()
      return this.listUnidades.filter(u =>
        String(u.id).includes(b) ||
        (u.material && u.material.toLowerCase().includes(b)) ||
        (u.rfid && u.rfid.toLowerCase().includes(b)) ||
        (u.color && u.color.toLowerCase().includes(b))
      )
    },
    estanteriasFiltradas() {
      if (!this.buscarEstanteria) return this.listEstanterias
      const b = this.buscarEstanteria.toLowerCase()
      return this.listEstanterias.filter(e =>
        (e.nombre && e.nombre.toLowerCase().includes(b)) ||
        (e.descripcion && e.descripcion.toLowerCase().includes(b))
      )
    },
    cajasFiltradas() {
      if (!this.filtroEstanteriaCajas) return this.listCajas
      return this.listCajas.filter(c => c.idEstanteria == this.filtroEstanteriaCajas.id)
    },
    materialesDistintos() {
      const ids = new Set()
      this.listUnidades.forEach(u => ids.add(u.idMaterial))
      return ids.size
    },
    tituloMovimiento() {
      if (this.movForm.tipo == 'descontar') return 'Descontar'
      if (this.movForm.tipo == 'reponer') return 'Reponer'
      return 'Ajustar'
    },
    iconoMovimiento() {
      if (this.movForm.tipo == 'descontar') return 'fa-solid fa-minus'
      if (this.movForm.tipo == 'reponer') return 'fa-solid fa-plus'
      return 'fa-solid fa-scale-balanced'
    },
    iconoResultado() {
      if (!this.rfidResultado) return ''
      if (this.rfidResultado.tipo == 'estanteria') return 'fa-solid fa-layer-group'
      if (this.rfidResultado.tipo == 'caja') return 'fa-solid fa-box'
      if (this.rfidResultado.tipo == 'unidad') return 'fa-solid fa-tag'
      return 'fa-solid fa-circle-question'
    },
  },
  watch: {
    tabIndex(nuevo) {
      if (nuevo == 3) this.loadHistorial()
    },
  },
  methods: {
    load() {
      axios.post('/uso', { seccion: 'almacen' })
      this.loadEstanterias()
      this.loadCajas()
      this.loadUnidades()
      axios.get('/materiales', { params: { sinImg: 1 } }).then(r => { this.listMateriales = r.data })
    },
    loadEstanterias() {
      axios.get('/almacen/estanterias').then(r => { this.listEstanterias = r.data })
    },
    loadCajas() {
      axios.get('/almacen/cajas').then(r => { this.listCajas = r.data })
    },
    loadUnidades() {
      axios.get('/almacen/unidades').then(r => { this.listUnidades = r.data })
    },
    loadHistorial() {
      const params = {}
      if (this.filtroHistMaterial) params.idMaterial = this.filtroHistMaterial.id
      if (this.filtroHistTipo) params.tipo = this.filtroHistTipo
      axios.get('/almacen/historial', { params }).then(r => { this.listHistorial = r.data })
    },
    nuevaLineaHist() {
      const hoy = new Date().toISOString().slice(0, 10)
      return { fecha: hoy, tipo: 'salida', cantidad: null }
    },
    abrirHistorialForm() {
      this.histForm = { unidadSelect: null, lineas: [this.nuevaLineaHist()] }
      this.showHistModal = true
    },
    agregarLineaHist() {
      this.histForm.lineas.push(this.nuevaLineaHist())
    },
    quitarLineaHist(i) {
      this.histForm.lineas.splice(i, 1)
    },
    guardarHistorial() {
      const f = this.histForm
      if (!f.unidadSelect) return this.toastError({ response: { data: { msg: 'Elige una unidad' } } })
      const lineas = f.lineas.filter(l => l.tipo && l.fecha)
      if (!lineas.length) return this.toastError({ response: { data: { msg: 'Añade al menos una línea con fecha y tipo' } } })
      this.guardandoHist = true
      axios.post('/almacen/historial', { idUnidad: f.unidadSelect.id, lineas })
        .then(() => {
          this.showHistModal = false
          this.toastOk('Historial añadido')
          this.loadUnidades()
          if (this.tabIndex == 3) this.loadHistorial()
        })
        .catch(this.toastError)
        .finally(() => { this.guardandoHist = false })
    },

    ubicacionTexto(u) {
      if (u.idCaja) return (u.caja || 'Caja ' + u.cajaRfid) + ' (' + u.cajaEstanteria + ', fila ' + u.cajaFila + ')'
      if (u.idEstanteria) return u.estanteria + ', fila ' + u.fila
      return 'Sin ubicación'
    },

    // -------- Impresión de etiquetas (graba el RFID en el chip) --------
    imprimirUnidad(u) {
      this.abrirImpresion({
        titulo: u.material,
        // El "(estantería, fila)" va en su propia línea (\n -> salto en la etiqueta)
        sub: this.ubicacionTexto(u).replace(' (', '\n('),
        rfid: u.rfid,
        id: u.id,
      })
    },
    imprimirCaja(c) {
      this.abrirImpresion({
        titulo: c.nombre || 'Caja',
        sub: c.estanteria + ', fila ' + c.fila,
        rfid: c.rfid,
      })
    },
    imprimirEstanteria(e) {
      this.abrirImpresion({ titulo: e.nombre, sub: '', rfid: e.rfid || '' })
    },
    abrirImpresion(datos) {
      this.printDatos = datos
      this.previewAlmacen = null
      this.showPrintModal = true
      this.cargarPreviewAlmacen()
    },
    cargarPreviewAlmacen() {
      this.cargandoPreview = true
      axios.post('/almacen/preview', { titulo: this.printDatos.titulo, sub: this.printDatos.sub, id: this.printDatos.id })
        .then(({ data }) => { if (data && data.image) this.previewAlmacen = data.image })
        .catch(() => { /* la vista previa no es crítica para imprimir */ })
        .finally(() => { this.cargandoPreview = false })
    },
    confirmarImprimir() {
      this.imprimiendo = true
      axios.post('/almacen/print', this.printDatos)
        .then(({ data }) => {
          if (data && data.ok) { this.toastOk('Etiqueta impresa'); this.showPrintModal = false }
          else this.toastError({ response: { data: { msg: (data && data.status) || 'No se pudo grabar el chip' } } })
        })
        .catch(this.toastError)
        .finally(() => { this.imprimiendo = false })
    },
    // La BD guarda DECIMAL(12,3): quita los ceros sobrantes (100.000 -> 100, 3.500 -> 3.5)
    fmt(cantidad) {
      if (cantidad === null || cantidad === undefined || cantidad === '') return ''
      return String(parseFloat(cantidad))
    },
    toastError(e) {
      const msg = (e.response && e.response.data && e.response.data.msg) || 'Algo ha ido mal'
      this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
    },
    toastOk(msg) {
      this.$bvToast.toast(msg, { title: 'Almacén', variant: 'success', solid: true })
    },

    // ---------- Escaneo RFID ----------
    buscarRfid() {
      if (!this.rfidBuscar) return
      const rfid = this.rfidBuscar.trim()
      axios.get('/almacen/rfid/' + encodeURIComponent(rfid))
        .then(r => { this.rfidResultado = r.data })
        .catch(() => { this.rfidResultado = { tipo: 'no-encontrado', rfid } })
      this.rfidBuscar = ''
    },

    // ---------- Estanterías ----------
    abrirEstanteriaForm(est) {
      this.estanteriaForm = est
        ? { id: est.id, rfid: est.rfid, nombre: est.nombre, filas: est.filas, descripcion: est.descripcion }
        : { id: null, rfid: '', nombre: '', filas: 1, descripcion: '' }
      this.showEstanteriaModal = true
    },
    guardarEstanteria() {
      const f = this.estanteriaForm
      if (!f.nombre || !f.filas) return this.toastError({ response: { data: { msg: 'Nombre y filas son obligatorios' } } })
      const req = f.id
        ? axios.put('/almacen/estanterias', f)
        : axios.post('/almacen/estanterias', f)
      req.then(() => {
        this.showEstanteriaModal = false
        this.toastOk(f.id ? 'Estantería actualizada' : 'Estantería creada')
        this.loadEstanterias()
      }).catch(this.toastError)
    },
    eliminarEstanteria(est) {
      this.$bvModal.msgBoxConfirm('¿Eliminar la estantería ' + est.nombre + '?', { okTitle: 'Eliminar', cancelTitle: 'Cancelar', okVariant: 'danger' })
        .then(ok => {
          if (!ok) return
          axios.post('/almacen/estanterias/delete', { id: est.id })
            .then(() => { this.toastOk('Estantería eliminada'); this.loadEstanterias() })
            .catch(this.toastError)
        })
    },

    // ---------- Cajas ----------
    abrirCajaForm(caja) {
      this.cajaForm = caja
        ? { id: caja.id, rfid: caja.rfid, nombre: caja.nombre, estanteriaSelect: this.listEstanterias.find(e => e.id == caja.idEstanteria) || null, fila: caja.fila }
        : { id: null, rfid: '', nombre: '', estanteriaSelect: null, fila: 1 }
      this.showCajaModal = true
    },
    guardarCaja() {
      const f = this.cajaForm
      if (!f.estanteriaSelect || !f.fila) return this.toastError({ response: { data: { msg: 'Estantería y fila son obligatorios' } } })
      const info = { id: f.id, nombre: f.nombre, idEstanteria: f.estanteriaSelect.id, fila: f.fila }
      const req = f.id
        ? axios.put('/almacen/cajas', info)
        : axios.post('/almacen/cajas', info)
      req.then(r => {
        this.showCajaModal = false
        this.toastOk(f.id ? 'Caja actualizada' : 'Caja creada · RFID: ' + r.data.rfid)
        this.loadCajas()
        this.loadUnidades()
        this.loadEstanterias()
      }).catch(this.toastError)
    },
    eliminarCaja(caja) {
      this.$bvModal.msgBoxConfirm('¿Eliminar la caja ' + (caja.nombre || caja.rfid) + '?', { okTitle: 'Eliminar', cancelTitle: 'Cancelar', okVariant: 'danger' })
        .then(ok => {
          if (!ok) return
          axios.post('/almacen/cajas/delete', { id: caja.id })
            .then(() => { this.toastOk('Caja eliminada'); this.loadCajas(); this.loadEstanterias() })
            .catch(this.toastError)
        })
    },
    verContenidoCaja(caja) {
      this.cajaContenido = caja
      axios.get('/almacen/unidades', { params: { idCaja: caja.id } })
        .then(r => { this.unidadesCaja = r.data; this.showContenidoModal = true })
        .catch(this.toastError)
    },

    // ---------- Unidades ----------
    // Al elegir material, precarga la cantidad inicial con su stock actual
    onMaterialElegido(m) {
      if (m && m.stock != null && m.stock !== '') this.unidadForm.cantidad = Number(m.stock)
    },
    abrirUnidadForm(u) {
      this.unidadForm = u
        ? {
            id: u.id,
            rfid: u.rfid,
            materialSelect: null,
            cantidad: u.cantidad,
            ubicacionTipo: u.idCaja ? 'caja' : 'estanteria',
            cajaSelect: this.listCajas.find(c => c.id == u.idCaja) || null,
            estanteriaSelect: this.listEstanterias.find(e => e.id == u.idEstanteria) || null,
            fila: u.fila || 1,
            nota: u.nota,
          }
        : { id: null, rfid: '', materialSelect: null, cantidad: 0, ubicacionTipo: 'caja', cajaSelect: null, estanteriaSelect: null, fila: 1, nota: '' }
      this.showUnidadModal = true
    },
    guardarUnidad() {
      const f = this.unidadForm
      const info = { id: f.id, nota: f.nota, idCaja: null, idEstanteria: null, fila: null }

      if (f.ubicacionTipo == 'caja') {
        if (!f.cajaSelect) return this.toastError({ response: { data: { msg: 'Selecciona una caja' } } })
        info.idCaja = f.cajaSelect.id
      } else {
        if (!f.estanteriaSelect || !f.fila) return this.toastError({ response: { data: { msg: 'Selecciona estantería y fila' } } })
        info.idEstanteria = f.estanteriaSelect.id
        info.fila = f.fila
      }

      let req
      if (f.id) {
        req = axios.put('/almacen/unidades', info)
      } else {
        if (!f.materialSelect) return this.toastError({ response: { data: { msg: 'El material es obligatorio' } } })
        info.idMaterial = f.materialSelect.id
        info.cantidad = f.cantidad
        req = axios.post('/almacen/unidades', info)
      }

      req.then(r => {
        this.showUnidadModal = false
        this.toastOk(f.id ? 'Unidad actualizada' : 'Unidad creada · RFID: ' + r.data.rfid)
        this.loadUnidades()
        this.loadCajas()
        this.loadEstanterias()
      }).catch(this.toastError)
    },
    eliminarUnidad(u) {
      this.$bvModal.msgBoxConfirm('¿Dar de baja esta unidad de ' + u.material + '?', { okTitle: 'Eliminar', cancelTitle: 'Cancelar', okVariant: 'danger' })
        .then(ok => {
          if (!ok) return
          axios.post('/almacen/unidades/delete', { id: u.id })
            .then(() => { this.toastOk('Unidad dada de baja'); this.loadUnidades(); this.loadCajas(); this.loadEstanterias() })
            .catch(this.toastError)
        })
    },
    verHistorialUnidad(u) {
      this.filtroHistMaterial = this.listMateriales.find(m => m.id == u.idMaterial) || null
      this.filtroHistTipo = null
      this.tabIndex = 3
      this.loadHistorial()
    },

    // ---------- Movimientos ----------
    abrirMovimiento(unidad, tipo) {
      this.movForm = { unidad, tipo, cantidad: null, nota: '' }
      this.showMovModal = true
    },
    guardarMovimiento() {
      const f = this.movForm
      if (f.cantidad === null || f.cantidad === '' || f.cantidad < 0) {
        return this.toastError({ response: { data: { msg: 'Indica una cantidad válida' } } })
      }
      let req
      if (f.tipo == 'ajustar') {
        req = axios.post('/almacen/unidades/ajustar', { id: f.unidad.id, cantidad: f.cantidad, nota: f.nota })
      } else {
        req = axios.post('/almacen/unidades/' + f.tipo, { id: f.unidad.id, cantidad: f.cantidad, nota: f.nota })
      }
      req.then(() => {
        this.showMovModal = false
        this.toastOk('Movimiento registrado')
        this.loadUnidades()
        this.rfidResultado = null
      }).catch(this.toastError)
    },
  },
  created() {
    this.load()
  },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$info: #06B6D4;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.almacen-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

// ============ Header ============
.alm-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

// ============ Scan box ============
.scan-box {
  position: relative;
  display: flex;
  align-items: center;
  width: 340px;
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  box-shadow: $shadow;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }

  &__icon {
    position: absolute;
    left: 12px;
    color: $primary;
    font-size: 13px;
    pointer-events: none;
  }

  &__input {
    flex: 1;
    border: none;
    background: transparent;
    padding: 9px 8px 9px 36px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: $text;

    &::placeholder {
      color: #CBD5E1;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 13px;
    }
    &:focus { outline: none; }
  }

  &__btn {
    border: none;
    background: $primary;
    color: white;
    width: 38px;
    align-self: stretch;
    border-radius: 0 ($radius-sm - 1) ($radius-sm - 1) 0;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.15s ease;

    &:hover { background: $primary-dark; }
  }
}

// ============ KPI Strip ============
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

// ============ Scan result ============
.scan-result {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: $card-bg;
  border: 1px solid $border;
  border-left: 3px solid $primary;
  border-radius: $radius-sm;
  box-shadow: $shadow;
  padding: 16px 18px;
  margin-bottom: 20px;

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    flex-shrink: 0;

    &--caja { background: rgba($info, 0.1); color: $info; }
    &--unidad { background: rgba($primary, 0.1); color: $primary; }
    &--no-encontrado { background: rgba($danger, 0.1); color: $danger; }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    display: block;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: $text;
    margin-bottom: 4px;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $text-muted;

    i { margin-right: 3px; }
  }

  &__list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__empty {
    display: block;
    margin-top: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #CBD5E1;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }

  &__close {
    border: none;
    background: transparent;
    color: $text-muted;
    cursor: pointer;
    font-size: 13px;
    padding: 4px 6px;
    border-radius: 6px;
    transition: all 0.15s ease;

    &:hover { background: $bg; color: $text; }
  }
}

.scan-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: $bg;
  border-radius: $radius-sm;

  &__name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: $text;
    flex: 1;
  }
}

// ============ Tabs ============
.alm-tabs {
  display: inline-flex;
  gap: 4px;
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm + 2;
  padding: 4px;
  box-shadow: $shadow;
  margin-bottom: 16px;
}

.alm-tab {
  border: none;
  background: transparent;
  padding: 8px 18px;
  border-radius: $radius-sm - 1;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.18s ease;
  display: inline-flex;
  align-items: center;
  gap: 7px;

  i { font-size: 11px; opacity: 0.7; }

  &:hover { color: $text; background: $bg; }

  &--active {
    background: $primary;
    color: white;

    i { opacity: 1; }
    &:hover { background: $primary-dark; color: white; }
  }
}

// ============ Buttons ============
.btn-alm {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: $radius-sm;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  i { font-size: 11px; }

  &--primary {
    background: $primary;
    color: white;
    &:hover { background: $primary-dark; }
  }

  &--danger-soft {
    background: rgba($danger, 0.08);
    color: $danger;
    &:hover { background: rgba($danger, 0.15); }
  }

  &--success-soft {
    background: rgba($success, 0.08);
    color: $success;
    &:hover { background: rgba($success, 0.15); }
  }

  &--soft {
    background: #F1F5F9;
    color: $text-muted;
    &:hover { background: #E2E8F0; }
  }
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.hist-lineas {
  &__head {
    display: grid;
    grid-template-columns: 1.2fr 1.4fr 1fr 32px;
    gap: 10px;
    margin-bottom: 6px;
    font-size: 11px;
    font-weight: 600;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  &__add { margin-top: 6px; }
}

.hist-linea {
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1fr 32px;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;

  &__del {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: none;
    background: rgba($danger, 0.08);
    color: $danger;
    cursor: pointer;
    &:hover { background: rgba($danger, 0.15); }
  }
}

// ============ List Card ============
.list-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    padding: 16px 22px;
    border-bottom: 1px solid $border;
  }

  &__hint {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $text-muted;
  }

  &__filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__footer {
    padding: 12px 22px;
    border-top: 1px solid $border;
  }
}

.footer-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

// ============ Search box ============
.search-box {
  position: relative;
  flex: 1;
  max-width: 320px;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-muted;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;
    transition: border-color 0.15s ease;

    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

// ============ Table ============
// Los ::v-deep van bajo .almacen-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.almacen-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 10px 16px;
  background: transparent;
}

.almacen-page ::v-deep .table-row td {
  padding: 10px 16px;
  vertical-align: middle;
  border-color: lighten($border, 3%);
  font-size: 13px;
}

.almacen-page ::v-deep .table-row:hover td {
  background: $bg;
}

.cell-nombre {
  display: flex;
  flex-direction: column;

  &__name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: $text;
    line-height: 1.2;
  }

  &__sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $text-muted;
  }
}

.cell-text {
  font-size: 13px;
  color: $text;
}

.cell-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text;
  font-feature-settings: "tnum";
  white-space: nowrap;
}

.cell-muted {
  color: #CBD5E1;
  font-size: 12px;
}

.stock-arrow {
  font-size: 9px;
  color: $text-muted;
  margin: 0 3px;
}

// ============ Chips & pills ============
.rfid-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  background: $bg;
  border: 1px solid $border;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 600;
  color: $text-muted;
  letter-spacing: 0.3px;
  white-space: nowrap;

  &--modal {
    font-size: 13px;
    padding: 7px 14px;
    color: $text;
  }
}

.qty-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  font-feature-settings: "tnum";
  white-space: nowrap;

  &--ok { background: rgba($success, 0.1); color: darken($success, 8%); }
  &--zero { background: rgba($danger, 0.1); color: $danger; }
}

.loc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba($primary, 0.07);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 600;
  color: $primary;
  white-space: nowrap;

  i { font-size: 9px; }
}

.badge-count {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba($info, 0.08);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  color: darken($info, 8%);
  white-space: nowrap;

  &--empty {
    background: $bg;
    color: #CBD5E1;
  }
}

.tipo-pill {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &--alta { background: rgba($primary, 0.1); color: $primary; }
  &--salida { background: rgba($danger, 0.1); color: $danger; }
  &--entrada { background: rgba($success, 0.1); color: darken($success, 8%); }
  &--movimiento { background: rgba($info, 0.1); color: darken($info, 8%); }
  &--ajuste { background: rgba($warning, 0.12); color: darken($warning, 12%); }
  &--baja { background: #F1F5F9; color: $text-muted; }
  &--nota { background: rgba($info, 0.1); color: darken($info, 8%); }
}

// ============ Action buttons ============
.cell-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 12px;
  flex-shrink: 0;

  &--view:hover {
    background: rgba($info, 0.1);
    color: $info;
    border-color: rgba($info, 0.2);
  }

  &--print:hover {
    background: rgba($primary, 0.1);
    color: $primary;
    border-color: rgba($primary, 0.2);
  }

  &--edit:hover {
    background: rgba($primary, 0.1);
    color: $primary;
    border-color: rgba($primary, 0.2);
  }

  &--success:hover {
    background: rgba($success, 0.1);
    color: $success;
    border-color: rgba($success, 0.2);
  }

  &--warning:hover {
    background: rgba($warning, 0.12);
    color: darken($warning, 8%);
    border-color: rgba($warning, 0.25);
  }

  &--delete:hover {
    background: rgba($danger, 0.1);
    color: $danger;
    border-color: rgba($danger, 0.2);
  }
}

// ============ Empty state ============
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #CBD5E1;

  i { font-size: 26px; }

  span {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
  }

  &--modal { padding: 20px 0; }
}

// ============ Slide transition ============
.slide-enter-active, .slide-leave-active {
  transition: all 0.25s ease;
  max-height: 400px;
  overflow: hidden;
}
.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
}

// ============ Responsive ============
@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
  .alm-header { flex-direction: column; align-items: flex-start; }
  .scan-box { width: 100%; }
  .alm-tabs { display: flex; overflow-x: auto; }
  .search-box { max-width: none; }
  .list-card__header { flex-direction: column; align-items: stretch; }
}
</style>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

// Vue-select (unscoped)
.almacen-page, .modal-alm {
  .select-styled {
    .vs__dropdown-toggle {
      min-height: 34px;
      padding: 0 0 2px 0;
      border-radius: 8px;
      border: 1px solid #E2E8F0;
      transition: border-color 0.15s ease;
    }

    .vs__search {
      font-size: 13px;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: #3B82F6;
      box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1);
    }
  }
}

.almacen-page .select-filter {
  min-width: 220px;

  &--small { min-width: 150px; }
}

// ============ Modales ============
.modal-alm {
  .modal-content {
    border-radius: 16px;
    border: 1px solid #E2E8F0;
    box-shadow: 0 20px 60px rgba(0,0,0,0.12), 0 8px 20px rgba(0,0,0,0.06);
    overflow: hidden;
  }

  .modal-header {
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
    padding: 18px 24px;

    .modal-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 16px;
      font-weight: 700;
      color: #1E293B;
    }
  }

  .modal-body { padding: 22px 24px; }

  .modal-footer {
    border-top: 1px solid #E2E8F0;
    padding: 14px 24px;
  }

  .modal-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .mfield {
    display: flex;
    flex-direction: column;
    gap: 5px;

    &__label {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #64748B;
      margin: 0;
    }

    &__input {
      border-radius: 8px !important;
      border: 1px solid #E2E8F0 !important;
      font-size: 13px !important;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;

      &:focus {
        border-color: #3B82F6 !important;
        box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1) !important;
      }

      &--mono {
        font-family: 'JetBrains Mono', monospace !important;
        font-size: 12px !important;
        letter-spacing: 0.3px;
      }

      &--big {
        font-family: 'JetBrains Mono', monospace !important;
        font-size: 18px !important;
        font-weight: 700;
        text-align: center;
        padding: 10px !important;
        height: auto !important;
      }
    }
  }

  .auto-rfid {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 8px;
    background: rgba(#3B82F6, 0.06);
    border: 1px dashed rgba(#3B82F6, 0.3);
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #3B82F6;

    i { font-size: 13px; }

    &--stack {
      flex-direction: column;
      align-items: center;
    }
  }

  .print-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 96px;
    padding: 14px;
    margin-bottom: 12px;
    background: repeating-linear-gradient(45deg, #F1F3F7 0 8px, #F7F8FB 8px 16px);
    border: 1px solid #E2E8F0;
    border-radius: 8px;

    &__img {
      max-width: 100%;
      max-height: 130px;
      background: #fff;
      padding: 5px;
      border-radius: 3px;
      box-shadow: 0 1px 8px rgba(0, 0, 0, 0.14);
      image-rendering: pixelated;
    }
  }

  .seg-control {
    display: flex;
    gap: 4px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 4px;

    &__btn {
      flex: 1;
      border: none;
      background: transparent;
      padding: 8px 10px;
      border-radius: 7px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 12px;
      font-weight: 600;
      color: #64748B;
      cursor: pointer;
      transition: all 0.15s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;

      i { font-size: 10px; opacity: 0.7; }

      &:hover { color: #1E293B; }

      &--active {
        background: #FFFFFF;
        color: #3B82F6;
        box-shadow: 0 1px 3px rgba(0,0,0,0.08);

        i { opacity: 1; }
      }
    }
  }

  .mov-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #F8FAFC;
    border-radius: 10px;

    &__icon {
      width: 38px;
      height: 38px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      flex-shrink: 0;

      &--descontar { background: rgba(#EF4444, 0.1); color: #EF4444; }
      &--reponer { background: rgba(#10B981, 0.1); color: #10B981; }
      &--ajustar { background: rgba(#F59E0B, 0.12); color: #D97706; }
    }

    &__name {
      display: block;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #1E293B;
    }

    &__meta {
      display: block;
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      color: #64748B;
      margin-top: 2px;
    }
  }
}

// ══════════ DARK MODE ══════════
// Comunes a la página y a los modales (los modales se montan en body)
.dark-layout .almacen-page,
.dark-layout .modal-alm {
  .select-styled .vs__dropdown-toggle { background: #161616; border-color: #2c2c2c; }
  .select-styled .vs__selected, .select-styled .vs__search { color: #d0d2d6; }
  .select-styled .vs__dropdown-menu { background: #242424; border-color: #3a3a3a; }
  .select-styled .vs__dropdown-option { color: #d0d2d6; }
  .select-styled .vs__dropdown-option--highlight { background: #2c2c2c; color: #fff; }
  .select-styled.vs--open .vs__dropdown-toggle { border-color: #3B82F6; }

  .rfid-chip { background: #161616; border-color: #2c2c2c; color: #8e9099; }
  .rfid-chip--modal { color: #e4e6eb; }
  .scan-item { background: #161616; }
  .scan-item__name { color: #e4e6eb; }
  .empty-state { color: #6e7079; }
  .btn-alm--soft { background: #242424; color: #b4b7bd; &:hover { background: #2c2c2c; } }
  .badge-count--empty { background: #242424; color: #6e7079; }
  .tipo-pill--baja { background: #242424; color: #8e9099; }
  .hist-lineas__head { color: #8e9099; }
}

.dark-layout .almacen-page {
  .header-title { color: #e4e6eb; }

  .scan-box { background: #1f1f1f; border-color: #2c2c2c; &:focus-within { border-color: #3B82F6; } }
  .scan-box__input { color: #d0d2d6; &::placeholder { color: #6e7079; } }

  .kpi-card { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }
  .kpi-value { color: #e4e6eb; }
  .kpi-label, .kpi-foot { color: #8e9099; }

  .scan-result { background: #1f1f1f; border-color: #2c2c2c; border-left-color: #3B82F6; box-shadow: none; }
  .scan-result__title { color: #e4e6eb; }
  .scan-result__meta { color: #8e9099; }
  .scan-result__empty { color: #6e7079; }
  .scan-result__close:hover { background: #242424; color: #e4e6eb; }

  .alm-tabs { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }
  .alm-tab:hover { background: #242424; color: #e4e6eb; }
  .alm-tab--active, .alm-tab--active:hover { background: #3B82F6; color: #fff; }
  .alm-tab--active:hover { background: #2563EB; }

  .list-card { background: #1f1f1f; border-color: #2c2c2c; box-shadow: none; }
  .list-card__header { border-bottom-color: #2c2c2c; }
  .list-card__footer { border-top-color: #2c2c2c; }
  .list-card__hint, .footer-count { color: #8e9099; }

  .search-box__input { background: #161616; border-color: #2c2c2c; color: #d0d2d6; &::placeholder { color: #6e7079; } &:focus { border-color: #3B82F6; } }

  // Tablas
  .table { background: #1f1f1f !important; }
  .table-head th { background: #1f1f1f !important; color: #6e7079 !important; border-bottom-color: #2c2c2c !important; }
  .table-row { background: #1f1f1f !important; }
  .table-row td { border-color: #2c2c2c !important; }
  .table-row:hover td { background: #242424 !important; }

  .cell-nombre__name { color: #e4e6eb; }
  .cell-nombre__sub, .stock-arrow { color: #8e9099; }
  .cell-text, .cell-mono { color: #d0d2d6; }
  .cell-muted { color: #6e7079; }
}

.dark-layout .modal-alm {
  .modal-content { border-color: #2c2c2c; }
  .modal-header { background: #161616; border-bottom-color: #2c2c2c; }
  .modal-header .modal-title { color: #e4e6eb; }
  .modal-footer { border-top-color: #2c2c2c; }
  .mfield__label { color: #8e9099; }
  .mfield__input {
    background: #161616 !important;
    border-color: #2c2c2c !important;
    color: #d0d2d6 !important;
    &:focus { border-color: #3B82F6 !important; }
  }

  // Fondo de la vista previa oscuro; la etiqueta en sí se queda blanca (imita la impresa)
  .print-preview { background: repeating-linear-gradient(45deg, #1a1a1a 0 8px, #202020 8px 16px); border-color: #2c2c2c; }

  .seg-control { background: #161616; border-color: #2c2c2c; }
  .seg-control__btn { color: #8e9099; &:hover { color: #e4e6eb; } }
  .seg-control__btn--active { background: #242424; color: #3B82F6; box-shadow: none; }

  .mov-info { background: #161616; }
  .mov-info__name { color: #e4e6eb; }
  .mov-info__meta { color: #8e9099; }
}
</style>
